import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Stack,
  Dialog,
  Drawer,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle, useMediaQuery, Tooltip, SvgIcon
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import EducationDetails from './AddEducation';
import { useAuth } from 'hooks/useAuthContext';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { deleteWorkHistory, deleteEducationHistory } from 'api/users';
import { useSnackbar } from 'components/snackbar';
import RemoveEducation from './RemoveEducationDialog';
import { endpoints } from 'utils/axios';
import { changeNameFormat, allCapitalize } from 'utils/Utils';
import EducationDetailsMobile from '../basic-details-mobile-ui/EducationDetailsMobile';
import { useTheme } from '@mui/material/styles';
import InfoNote from './InfoNote';
import { EditSvgIcon, DeleteSvg } from 'components/icon/icon';
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog';

/**
 * EducationSection component
 *
 * This component renders the education section of a user's profile,
 * displaying a list of educational qualifications and allowing for
 * modifications such as adding, editing, or deleting entries.
 *
 * @param {object} item - The current education item.
 * @param {array} educationData - Array of education objects.
 * @param {function} onCancel - Callback when cancel action is triggered.
 * @param {object} expandedSection - The currently expanded section.
 * @param {function} handleExpand - Function to handle section expansion.
 * @param {function} onEducationDataChange - Callback when education data changes.
 * @param {array} certificateData - Array of certificate objects.
 * @param {array} workExperienceData - Array of work experience objects.
 * @param {boolean} showEdu - Flag to show/hide education details.
 * @param {function} handleShowEdu - Function to handle showing education details.
 * @param {function} handleEducationDelete - Callback for deleting an education entry.
 * @param {boolean} uploadResume - Flag indicating if resume upload is enabled.
 *
 * @returns {ReactElement} The rendered education section component.
 */

const EducationSection = ({ item, educationData, onCancel, expandedSection, handleExpand, onEducationDataChange, certificateData, workExperienceData, showEdu, handleShowEdu, handleEducationDelete, uploadResume }) => {
  const [showEducation, setShowEducation] = useState(false);
  const [educations, setEducations] = useState([]);
  const [actionType, setActionType] = useState('');
  const [open, setOpen] = useState(false);
  const [itemId, setItemId] = useState('');
  const [title, setTitle] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [selectedCard, setSelectedCard] = useState(null);
  const [idForScroll, setIdForScroll] = useState(null);
  const [scroll, setScroll] = useState(false);
  const educationRef = useRef(null);
  const detailsRef = useRef(null);
  // const [openDetailsDrawer, setOpenDetailsDrawer] = React.useState(false);
  const [degreeData, setDegreeData] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, updateContext } = useAuth();
  const [isReadOnly, setReadOnly] = useState(showEdu ? true : false);

  useEffect(() => {
    fetchDegreeData();
  }, []);

  useEffect(() => {
    const newWork = educationData?.filter(data =>
      !educations?.some(history => history._id === data._id)
    );
    if (educationData && educationData?.length > 0) setIdForScroll(newWork[0]?._id || null);
    setEducations(educationData);
  }, [educationData]);
  useEffect(() => {
    if (idForScroll && scroll) {
      scrollToChild(idForScroll);
      setIdForScroll(null);
    }
  }, [idForScroll]);

  useLayoutEffect(() => {
    if (expandedSection?.section === 'education' && detailsRef?.current) {
      detailsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [detailsRef, expandedSection]);

  /**
   * Scrolls smoothly to a child element with the specified targetId.
   * If the element exists, it will be scrolled into view smoothly,
   * aligning the start of the element with the start of the scrollable area.
   * 
   * @param {string} targetId - The ID of the element to scroll to.
   */

  const scrollToChild = (targetId) => {
    if (targetId) {
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  };

  /**
   * Handles the click event on an education card.
   * Expands or collapses the corresponding card section and
   * sets the selected card and read-only state if applicable.
   * For mobile devices, it also opens the Details drawer.
   * @param {object} item the education data
   * @param {number} index the index of the education card
   */
  const handleCardClick = (item, index) => {
    handleExpand('education', index);
    setSelectedCard(item);
    if (isMobile) {
      handleOpenDrawer();
      setShowEducation(true);
    } else {
      setShowEducation(false);
    }
    item?.status !== 'active' && setReadOnly(false);
  };

  /**
   * Scrolls smoothly to the education section.
   * If the element exists, it will be scrolled into view smoothly,
   * aligning the start of the element with the start of the scrollable area.
   */
  const scrollToEducation = () => {
    educationRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  /**
   * Handles the deletion of an education entry.
   * If the user is at stage 3 or higher and there is only one education entry, it shows an error message.
   * If no educationId is provided, it removes the 'education' entry from local storage and updates the state.
   * If an educationId is provided, it prepares the UI for deletion confirmation by setting the action type, title, and itemId, and opens the confirmation dialog.
   * 
   * @param {string} educationId - The ID of the education entry to delete.
   * @param {string} title - The title of the education entry to delete.
   * @param {Event} event - The event that triggered the delete action.
   */

  const handleDeleteEducation = async (educationId, title, event) => {
    event.stopPropagation();
    if (user?.stages >= 3 && educations.length === 1) {
      enqueueSnackbar('The Education field cannot be empty', { variant: 'error' });
      return;
    }
    if (!educationId) {
      localStorage.removeItem("education");
      setEducations([...educations?.filter(edu => edu?._id)]);
      setSelectedCard(null);
    } else {
      setActionType('education');
      setTitle(title);
      setItemId(educationId);
      handleOpenRemove();
    }
  };

  /**
   * Handles the data change event for the education section.
   * If the user has made changes, it updates the education data and scrolls to the education section.
   * It also closes the details drawer.
   * @param {boolean} isChanged - Whether the education data has been changed or not.
   */
  const onHandleDataChange = async (isChanged, educationId) => {

    handleCloseDrawer();
    setShowEducation(false);
    if (isChanged) {
      await onEducationDataChange();
      setScroll(true);
      if (educationId) setIdForScroll(educationId);
    }
  };

  /**
   * Closes the remove confirmation dialog and resets the itemId state.
   */
  const handleClose = () => {
    handleCloseRemove();
    setItemId('');
  };
  /**
   * Handles the opening and closing of the education details drawer.
   * @param {boolean} newOpen - Whether the drawer should be opened or closed.
   */
  // const handleOpenDrawer = (newOpen) => {
  //   setOpenDetailsDrawer(newOpen);
  // };

  /**
   * Handles the deletion of an education entry.
   * If showEdu is true, calls the handleDeleteEdu function and closes the dialog.
   * If actionType is 'education', calls the deleteEducationHistory function and closes the dialog if the response is successful.
   * It also shows an error message if the response is not successful.
   */
  const handleDeleteAction = async () => {
    if (showEdu) {
      handleDeleteEdu(itemId);
      return;
    }
    if (actionType === 'education') {
      const response = await deleteEducationHistory({ educationId: itemId });
      if (response && response?.success) {
        setEducations(response?.data || []);
        onEducationDataChange();
        handleCloseDrawer();
      } else {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    }
    handleClose();
  };

  /**
   * Handles the addition of a new education item.
   * Sets showEducation to true, expands the education section with the given index, and resets the selectedCard state variable to an empty object.
   */
  const handleAddNewEdu = () => {
    setShowEducation(true);
    handleExpand("education", educations.length);
    setSelectedCard({});
  }
  /**
   * Fetches the degree data from the server and sets the degree data state to the fetched data.
   * The degree data is deduplicated using a Set.
   * @memberof EducationSection
   * @returns {Promise<void>} - Resolves when the degrees are fetched successfully, rejects if there's an error.
   */
  const fetchDegreeData = async () => {
    const response = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.degrees}`);
    const resultData = await response.json();
    setDegreeData([...new Set([...resultData])]);
  };
  /**
   * Checks for overlapping date ranges across education, certificate, and work experience data.
   *
   * This function verifies if the given education data overlaps with any existing entries
   * in the education, certificate, or work experience datasets. It performs the check
   * by iterating over filtered education data, certificate data, and work experience data
   * and comparing the date ranges.
   *
   * @param {object} data - The education data to check for overlaps, containing `education_from` 
   *                        and `education_to` date fields.
   * @returns {object} - Returns an object indicating whether an overlap was found, and includes
   *                     details about the type and degree of the overlapping item if applicable.
   *                     Example: { isOverlapping: true, type: 'education', degree: 'Bachelor' }
   *                     If no overlap is found, returns { isOverlapping: false }.
   */

  const checkingOverlapping = (data) => {
    if (data?.learning_format !== "Full time - offline") return { isOverlapping: false };

    const checkOverlap = (items, fromKey, toKey, typeLabel, degreeKey) => {
      for (let i = 0; i < items.length; i++) {
        const fromDate = new Date(items[i][fromKey]);
        const toDate = typeLabel === 'work experience' ? (items[i]?.present ? new Date() : new Date(items[i][toKey])) : new Date(items[i][toKey]);
        const dataFromDate = new Date(data?.education_from);
        const dataToDate = new Date(data?.education_to);

        if (fromDate <= dataToDate && dataFromDate <= toDate) {
          return { isOverlapping: true, type: typeLabel, degree: items[i][degreeKey] };
        }
      }
      return { isOverlapping: false };
    };

    const filterEducation = educationData?.filter(edu => edu?._id !== data?._id && edu?.learning_format === "Full time - offline");
    const educationOverlap = checkOverlap(filterEducation, 'education_from', 'education_to', 'education', 'degree_received');
    if (educationOverlap.isOverlapping) return educationOverlap;

    const filterCertificate = certificateData?.filter(edu => edu?._id !== data?._id && edu?.learning_format === "Full time - offline");
    const certificateOverlap = checkOverlap(filterCertificate, 'education_from', 'education_to', 'certificate', 'certificate_received');
    if (certificateOverlap.isOverlapping) return certificateOverlap;

    const filterWorkExperienceData = workExperienceData?.filter(edu => edu?._id !== data?._id && edu?.organization_name !== 'Work Gap');

    const workOverlap = checkOverlap(filterWorkExperienceData, 'work_history_from', 'work_history_to', 'work experience', 'last_job_function');
    if (workOverlap.isOverlapping) return workOverlap;

    return { isOverlapping: false };
  };

  /**
   * Handles the saving of education data when the user clicks the "Save and add to resume" button.
   * Removes the education data from local storage, closes the education details drawer, and calls
   * the handleShowEdu function to add the education data to the resume.
   * @param {object} education - The education data to be saved.
   */
  const handleEducationResume = (education, id) => {
    localStorage.removeItem('education');
    setShowEducation(false);
    handleCloseDrawer();
    setScroll(true);
    if (id) setIdForScroll(id);
    handleShowEdu('Education', education);
  }

  /**
   * Handles the deletion of an education item.
   * Calls the handleEducationDelete function, closes the remove confirmation dialog, and closes the education details drawer.
   * @param {string} eduId - The ID of the education item to be deleted.
   */
  const handleDeleteEdu = (eduId) => {
    handleEducationDelete(eduId);
    handleClose();
    handleCloseDrawer();
  };

  const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
  const dialogIds = dialogLocation.state?.dialogIds || [];

  const openDetailsDrawer = dialogIds.includes("education");
  const openRemove = dialogIds.includes("removeEducation");

  useEffect(() => {
    if (!openDetailsDrawer) {
      setShowEducation(false);
      setSelectedCard(null);
      onCancel();
      onHandleDataChange();
      showEdu && setReadOnly(true);
    }
  }, [openDetailsDrawer]);

  const handleCloseDrawer = () => {
    const timer = setTimeout(() => {
      closeDialog('education');
    }, 300);
  };
  const handleOpenDrawer = () => {
    openDialog('education');
  };
  const handleCloseRemove = () => {
    closeDialog('removeEducation');
  };
  const handleOpenRemove = () => {
    openDialog('removeEducation');
  };

  return (
    <Box className="candidate-basic-det-wrapper" sx={{ position: 'relative' }}>
      <Box mb={1.5}>
        <Typography className="step-heading xs-fs-16" variant="h6" gutterBottom>
          Your Education
        </Typography>
        <Typography className="step-info xs-fs-12" variant="body2" gutterBottom>
          {uploadResume ? 'Please provide details of your latest educational qualification (mandatory). You may also add other degrees & qualifications (recommended).' : 'Add your highest education degree first'}
        </Typography>
      </Box>
      <Box mb={2}>
        {educations.length !== 0 &&
          educations.map((item, index) => (
            <React.Fragment key={item._id || index}>
              {expandedSection?.section !== 'education' || expandedSection.index !== index ? (
                <Box className={showEdu && "update-resume-accord-wrapper"}>
                  <Card id={idForScroll === item?._id ? idForScroll : null} className={`info-list-item ${item?.status !== "active" ? "incomplete-details" : ""}`} variant="outlined" key={item._id} sx={{ marginTop: 1 }} onClick={() => handleCardClick(item, index)}>
                    <CardContent sx={{ padding: showEdu && '0!important' }}>
                      <Box className="job-update-form-top">
                        <Box className="details-info-header" mb={2.5} sx={{ marginBottom: showEdu ? '20px !important' : '0px !important' }}>
                          <Stack direction="row" alignItems={'center'} justifyContent={'space-between'} spacing={2}>
                            <Box>
                              <Stack direction="row" alignItems={'center'} spacing={1.5}>
                                <Typography variant="h6" className="card-header-title">
                                  {item.degree_received} at {allCapitalize(item.education_institute_name)}
                                </Typography>
                                {item?.status !== "active" && <Box className="incomplete-tag">Incomplete</Box>}
                              </Stack>
                              <Typography className="info-wrapper" variant="body2" gutterBottom>
                                {item.education_from ? `${dayjs(item.education_from).format('MMM YYYY')} - ${item.education_to ? dayjs(item.education_to).format('MMM YYYY') : 'Present'}` : ''}
                              </Typography>
                            </Box>
                            <Stack direction="row" alignItems={'center'} spacing={2} >
                              {item?.status !== "active" &&
                                <IconButton
                                  className="delete-sec-btn"
                                  aria-label="delete"
                                  onClick={(event) => handleDeleteEducation(item._id, item.degree_received, event)}
                                >
                                  <DeleteSvg />
                                </IconButton>}

                              <KeyboardArrowDownOutlinedIcon />
                            </Stack>
                          </Stack>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              ) : (
                <Box className={isReadOnly && "update-resume-accord-wrapper"} ref={detailsRef}>
                  <Card className={isReadOnly ? "info-list-item" : "form-wrapper"} variant="outlined">
                    <CardContent sx={{ padding: isReadOnly ? '0!important' : '24px !important' }}>
                      <EducationDetails
                        key={item?._id}
                        educationData={educations.find((_, idx) => idx === expandedSection.index)}
                        id={item?._id}
                        onCancel={() => {
                          setShowEducation(false);
                          setSelectedCard(null);
                          onCancel()
                        }}
                        handleArrowClick={() => {
                          onCancel();
                          showEdu && setReadOnly(true);
                        }}
                        onEducationDataChange={onHandleDataChange}
                        degreeData={degreeData}
                        checkingOverlapping={checkingOverlapping}
                        showEdu={showEdu}
                        handleShowEdu={handleEducationResume}
                        handleDeleteEdu={handleDeleteEdu}
                        educationLength={educations.length}
                        isReadOnly={isReadOnly}
                        setReadOnly={setReadOnly}
                      />
                    </CardContent>
                  </Card>
                </Box>
              )}
            </React.Fragment>
          ))}
      </Box>
      {(showEducation && expandedSection?.section === 'education' &&
        (<Box className={isReadOnly && "update-resume-accord-wrapper"} ref={detailsRef}>
          <Card className={isReadOnly ? "info-list-item" : "form-wrapper"} variant="outlined">
            <CardContent sx={{ padding: isReadOnly ? '0!important' : '24px !important' }}>
              <EducationDetails
                onEducationDataChange={onHandleDataChange}
                onCancel={() => {
                  setShowEducation(false);
                  setSelectedCard(null);
                  onCancel();
                }}
                handleArrowClick={() => {
                  onCancel();
                  showEdu && setReadOnly(true);
                }}
                degreeData={degreeData}
                checkingOverlapping={checkingOverlapping}
                showEdu={showEdu}
                handleShowEdu={handleEducationResume}
                handleDeleteEdu={handleDeleteEdu}
                educationLength={educations.length}
                isReadOnly={isReadOnly}
                setReadOnly={setReadOnly}
              />
            </CardContent>
          </Card>
        </Box>
        ))}
      <Box mt={1.5} >
        {(!selectedCard || selectedCard?.status || expandedSection?.section !== 'education') &&
          <Button
            className="add-btn"
            disableRipple
            startIcon={<AddIcon />}
            disabled={educations.some(edu => !edu?._id)}
            onClick={() => {
              if (isMobile) {
                handleOpenDrawer(true);
                setShowEducation(true);
                handleExpand("education", educations?.length);
                setSelectedCard({});
              } else {
                handleAddNewEdu();
              }
              setReadOnly(false);
            }}
          >
            Add Education
          </Button>
        }
      </Box>
      {/* open add and update education in mobile view */}
      {openDetailsDrawer && (
        <Drawer className="mob-side-panel" open={openDetailsDrawer} anchor={'right'} >
          <EducationDetailsMobile
            educationData={selectedCard}
            id={selectedCard?._id}
            onCancel={() => {
              setShowEducation(false);
              setSelectedCard(null);
              localStorage.removeItem('education');
              onCancel();
              // handleCloseDrawer();
            }}
            handleArrowClick={() => {
              onHandleDataChange(true);
              // handleCloseDrawer();
              onCancel();
              showEdu && setReadOnly(true);
            }}
            onEducationDataChange={onHandleDataChange}
            degreeData={degreeData}
            handleDeleteEducation={handleDeleteEducation}
            selectedCard={selectedCard}
            checkingOverlapping={checkingOverlapping}
            educationLength={educations.length}
            showEdu={showEdu}
            handleDeleteEdu={handleDeleteEdu}
            isReadOnly={isReadOnly}
            setReadOnly={setReadOnly}
            handleShowEdu={handleEducationResume}
            deleteIncompleteSection={() => {
              setShowEducation(false);
              setSelectedCard(null);
              localStorage.removeItem('education');
              onCancel();
              handleCloseDrawer();
            }}
          />
        </Drawer>
      )}
      {/* remove education */}
      {isMobile ?
        <Drawer
          anchor="bottom"
          open={openRemove}
          onClose={handleClose}
          className="forgot-password-dialog otp-dialog-wrapper"
        >
          <RemoveEducation onClose={handleClose} onRemove={handleDeleteAction} title={title} />

        </Drawer> :
        <Dialog
          open={openRemove}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="otp-dialog-wrapper"
        >
          <RemoveEducation onClose={handleClose} onRemove={handleDeleteAction} title={title} />
        </Dialog>}
    </Box >
  );
};

export default EducationSection;
